/**
 * Static option lists for the lead-qualification onboarding form (AIB-6641).
 *
 * Option ids are persisted as-is — keep them stable. Labels are user-facing
 * and routed through `intl.formatMessage` *only when they aren't already
 * proper nouns* (CAD/CAM tool names are kept verbatim across locales).
 */

export interface Option<T extends string = string> {
  id: T;
  label: string;
}

export const ROLE_OPTIONS: Option[] = [
  { id: 'design_engineer', label: 'Design Engineer' },
  { id: 'simulation_engineer', label: 'Stress / Simulation Engineer' },
  { id: 'manufacturing_engineer', label: 'Manufacturing Engineer' },
  { id: 'cnc_programmer', label: 'CNC Programmer' },
  { id: 'additive_engineer', label: 'Additive Manufacturing Engineer' },
  { id: 'operations_manager', label: 'Operations / Production Manager' },
  { id: 'rd_innovation', label: 'R&D / Innovation' },
  { id: 'it_systems', label: 'IT / Systems' },
  { id: 'leadership', label: 'Leadership / Executive' },
  { id: 'other', label: 'Other' },
];

export const INDUSTRY_OPTIONS: Option[] = [
  { id: 'aerospace', label: 'Aerospace' },
  { id: 'automotive', label: 'Automotive' },
  { id: 'medical', label: 'Medical Devices' },
  { id: 'industrial', label: 'Industrial Manufacturing' },
  { id: 'consumer', label: 'Consumer Products' },
  { id: 'energy', label: 'Energy' },
  { id: 'other', label: 'Other' },
];

export const COMPANY_SIZE_OPTIONS: Option[] = [
  { id: '1-10', label: '1\u201310' },
  { id: '11-50', label: '11\u201350' },
  { id: '51-200', label: '51\u2013200' },
  { id: '201-1000', label: '201\u20131000' },
  { id: '1000+', label: '1000+' },
];

export interface IntentOption {
  id: string;
  label: string;
  description: string;
}

export const INTENT_OPTIONS: IntentOption[] = [
  {
    id: 'design',
    label: 'Design & Engineering',
    description: 'Design, simulation, and optimization',
  },
  {
    id: 'manufacturing',
    label: 'Manufacturing',
    description: 'Machining, additive, and production workflows',
  },
  {
    id: 'data',
    label: 'Data & Integration',
    description: 'Connect systems and automate data flows',
  },
  {
    id: 'operations',
    label: 'Operations',
    description: 'Planning, quality, and production optimization',
  },
  {
    id: 'exploration',
    label: 'Exploration',
    description: "I'm exploring what's possible",
  },
  { id: 'other', label: 'Other', description: 'Something else' },
];

export const CAD_OPTIONS: Option[] = [
  { id: 'solidworks', label: 'SolidWorks' },
  { id: 'fusion360', label: 'Fusion 360' },
  { id: 'catia', label: 'CATIA' },
  { id: 'siemens_nx', label: 'Siemens NX' },
  { id: 'creo', label: 'Creo' },
  { id: 'onshape', label: 'Onshape' },
];

export const CAE_OPTIONS: Option[] = [
  { id: 'ansys', label: 'Ansys' },
  { id: 'abaqus', label: 'Abaqus' },
  { id: 'comsol', label: 'COMSOL' },
  { id: 'simulink', label: 'Simulink' },
  { id: 'none', label: 'None' },
];

export const PLM_OPTIONS: Option[] = [
  { id: 'teamcenter', label: 'Teamcenter' },
  { id: 'windchill', label: 'Windchill' },
  { id: '3dexperience', label: '3DEXPERIENCE' },
  { id: 'none', label: 'None' },
];

export const CAM_OPTIONS: Option[] = [
  { id: 'mastercam', label: 'Mastercam' },
  { id: 'fusion360_cam', label: 'Fusion 360 CAM' },
  { id: 'powermill', label: 'PowerMill' },
  { id: 'hypermill', label: 'HyperMill' },
  { id: 'nx_cam', label: 'NX CAM' },
  { id: 'catia_manufacturing', label: 'CATIA Manufacturing' },
  { id: 'creo_cam', label: 'Creo CAM' },
  { id: 'solidcam', label: 'SolidCAM' },
  { id: 'camworks', label: 'CAMWorks' },
];

export const MFG_SETUP_OPTIONS: Option[] = [
  { id: 'cnc_in_house', label: 'CNC machining (in-house)' },
  { id: 'cnc_outsourced', label: 'CNC machining (outsourced)' },
  { id: 'additive', label: 'Additive manufacturing' },
  { id: 'injection_molding', label: 'Injection molding' },
  { id: 'casting_forming', label: 'Casting / forming' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'no_production', label: 'No production' },
  { id: 'other', label: 'Other' },
];

export const MES_OPTIONS: Option[] = [
  { id: 'siemens_opcenter', label: 'Siemens Opcenter' },
  { id: 'tulip', label: 'Tulip' },
  { id: 'plex', label: 'Plex' },
  { id: 'none', label: 'None' },
];

export const TOTAL_STEPS = 4;
